/**
 * Utility to create Apollo GraphQL client.
 *
 * This utility creates a singleton instance of Apollo client to leverage
 * query caching.
 */

// We allow our data-access infrastructure to reference apollo-client.
// eslint-disable-next-line no-restricted-imports -- we need to use apollo-client for this
import {ApolloClient} from "apollo-client";
import {ApolloLink} from "apollo-link";
import {BatchHttpLink} from "apollo-link-batch-http";
import {onError} from "apollo-link-error";
import {createHttpLink} from "apollo-link-http";
import stringHash from "string-hash";

import {khanFetch} from "@khan/data-fetch";
import {getGqlEndpoint} from "@khan/data-gql-core";
import Log, {Errors, KAError} from "@khan/logging";
import {DeployInfo, InitialRequestUrl, RequestInfo} from "@khan/shared-core";

import type {
    ApolloClientOptions,
    ApolloClient as ApolloClientType,
    ClientContext,
    FastlyCaching,
} from "../types.ts";

import checkMockedGql from "./check-mocked-gql.ts";
import createInMemoryCache from "./create-in-memory-cache.ts";

type NewApolloClientFn = (options: ApolloClientOptions) => ApolloClientType;

type FastlyCachingValue =
    | "persist_until_publish"
    | "persist_across_publish"
    | "persist_across_deploy";

const fastlyCachingMap: Partial<Record<FastlyCaching, FastlyCachingValue>> = {
    PERSIST_UNTIL_PUBLISH: "persist_until_publish",
    PERSIST_ACROSS_PUBLISH: "persist_across_publish",
    PERSIST_ACROSS_DEPLOY: "persist_across_deploy",
};

// The singleton ApolloClientType object for each module, or null
// if the singleton has not been created yet.
const singletons: Record<string, ApolloClientType> = {};

// See installOverrideClient() below. We use this when we want to query demo
// data instead of live data.
let _overrideApolloClientSingleton: ApolloClientType | null | undefined = null;

let _overrideNewApolloClient: NewApolloClientFn | null | undefined = null;

/**
 * A utility for logging strings to the console. In test mode we want to
 * produce a console error so that Jest complains in way that's easy to
 * surface. But we don't want this in production as it could trip up our e2e
 * tests which error out when they see a console error.
 *
 * @param  {Array<string>} msgs message strings to log
 */
const logError = (message: string, errorOrErrors: Error | Array<Error>) => {
    if (process.env.NODE_ENV === "test") {
        if (Array.isArray(errorOrErrors)) {
            Log.error(message, Errors.Internal, {
                metadata: {errors: JSON.stringify(errorOrErrors)},
            });
        } else {
            Log.error(message, Errors.Internal, {cause: errorOrErrors});
        }
    } else {
        Log.log(message, {
            error: JSON.stringify(errorOrErrors),
        });
    }
};

const errorLink = onError(({networkError, graphQLErrors}) => {
    if (networkError) {
        logError("GraphQL Network Error: ", networkError);
    }

    if (graphQLErrors) {
        logError("GraphQL Data Error: ", graphQLErrors);
    }
});

interface URIData {
    fastlyCachingValue: FastlyCachingValue | null | undefined;
    targetLocale: string | null | undefined;
    curriculum: string | null | undefined;
}

// Based on https://github.com/apollographql/apollo-link/blob/master/packages/apollo-link-http-common/src/index.ts
// I don't know if this is documented, but it shows that uri doesn't
// need to be a constant, it can be a function.  And looking at apollo-link
// this uri isn't consumed by the library when you have a custom fetcher,
// it's just passed in.  So we return an object that lets us construct the
// url in fetchWithOpname and batchFetchWithOpname.
const getGraphqUriData = (operation: any): URIData => {
    const context = operation.getContext();
    const fastlyCaching: FastlyCaching = context?.fastlyCaching;

    // TODO(jangmi): We could strip cookies from the requests to Fastly cached
    // routes as an extra security measure but we couldn't find a clean way to
    // achieve that. Fastly cached routes raise error when the query access
    // any user specific data so we rely on that for now but it would be nice
    // to revisit this later.
    const fastlyCachingValue: Nullable<FastlyCachingValue> = fastlyCaching
        ? fastlyCachingMap[fastlyCaching]
        : undefined;

    const targetLocale = context?.targetLocale;
    const curriculum = context?.curriculum;

    return {
        fastlyCachingValue,
        targetLocale,
        curriculum,
    };
};

function getUri(
    uriData: URIData,
    opname: string,
    queryParams: Record<string, string>,
): string {
    const {fastlyCachingValue, targetLocale, curriculum} = uriData;

    // We first substitute in the opname into the uri (as a query param,
    // or as part of the uri path)
    let uri = getUriWithOpname(fastlyCachingValue, opname);

    // We allow devs to set a schema mode by adding ?schemaMode=<mode>
    // to the url when loading a page. This allows them to test how a
    // page might look if a different service manages a field. Valid
    // schemaModes are 'normal', 'preview' and 'manual'.
    if (InitialRequestUrl.configParams.schemaMode) {
        queryParams["schemaMode"] = InitialRequestUrl.configParams
            .schemaMode as string;
    }

    // We allow to override the default request locale, enabling to target a
    // different localized topic tree.  If no targetLocale is provide, khanFetch
    // will add it.
    if (targetLocale) {
        queryParams["lang"] = targetLocale;
    }

    // We allow to override the default request curriculum, enabling to target a
    // different curriculum.  If no curriculum is provided, then it will be
    // determined by Fastly (this means that if it's missing for SSR, things
    // may be strange - in reality, this should always be there if module
    // is there because of how we setup context information).
    if (curriculum) {
        queryParams["curriculum"] = curriculum;
    }

    // Finally add any query params to the uri we will fetch.
    // TODO(somewhatabsract): Surely we should leverage the built in URI type
    // for this.
    if (Object.keys(queryParams).length > 0) {
        const separator = /\?/.test(uri) ? "&" : "?";
        const params = Object.keys(queryParams)
            .sort()
            .map((key) => key + "=" + queryParams[key])
            .join("&");
        uri = uri + separator + params;
    }
    return uri;
}

function getUriWithOpname(
    fastlyCachingValue: FastlyCachingValue | null | undefined,
    opname: string,
) {
    const queryParams = new URLSearchParams();
    const endpointUrl = getGqlEndpoint();

    const uri =
        endpointUrl.indexOf("__opname__") > -1
            ? endpointUrl.replace(/__opname__/g, opname)
            : queryParams.set("opname", opname);

    if (fastlyCachingValue && !RequestInfo.IS_DEV_SERVER) {
        queryParams.set("fastly_cacheable", fastlyCachingValue);

        // sync-start:gql-pcv-param 680688278 ./libs/data/gql/src/get-gql-uri.ts
        if (fastlyCachingValue === "persist_until_publish") {
            // If we're persisting until publish, we include the published
            // content version (pcv) as a query param to ensure we cache bust
            // on publish. This helps ensure that SSR retrieves the correct
            // information, rather than a stale cached value.
            queryParams.set("pcv", DeployInfo.commitSHA);
        }
        // sync-end:gql-pcv-param
    }

    return `${uri}${queryParams.size > 0 ? `?${queryParams.toString()}` : ""}`;
}

// Based on https://github.com/apollographql/apollo-link/tree/master/packages/apollo-link-http#dynamic-uri
const fetchWithOpname = (uriData: URIData, options: any) => {
    const {operationName, variables, query} = JSON.parse(options.body);

    // A check for verifying if createClient should be mocked out. This is
    // used to make sure that our GraphQL framework pieces aren't being used
    // in tests without being mocked out first. This function should only be
    // used directly from create-client.js.
    checkMockedGql({query, variables});

    // Normally, we POST to the endpoint, but when it's Fastly-cacheable,
    // we do a GET.
    if (
        uriData.fastlyCachingValue &&
        // NOTE #1: We disable Fastly caching in the dev environment.  This improves
        // performance when making changes to queries using Fastly caching since
        // we don't have to wait for the safelist to be recompute.  It also solves
        // an issue when using "Prod Backend" services where we can't recompute
        // the safelist.
        // sync-start:dev-cached-query 1524233751 ./libs/data/gql/src/get-gql-uri.ts
        !RequestInfo.IS_DEV_SERVER
        // sync-end:dev-cached-query
    ) {
        const queryParams: Record<string, string> = {
            // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'string'.
            hash: stringHash(query),
        };

        if (variables && Object.keys(variables).length > 0) {
            queryParams["variables"] = encodeURIComponent(
                JSON.stringify(variables),
            );
        }
        const uri = getUri(uriData, operationName, queryParams);

        /**
         * The non-default endpoint types are fastly cacheable and therefore,
         * SSR-safe. So let's ensure that khanFetch knows that.
         */
        return khanFetch(uri);
    }

    const uri = getUri(uriData, operationName, {});
    return khanFetch(uri, options);
};

// Based on https://github.com/apollographql/apollo-link/tree/master/packages/apollo-link-batch-http#dynamic-uri
const batchFetchWithOpname = (uriData: URIData, options: any) => {
    const operationNames = JSON.parse(options.body)
        .map((operation) => operation.operationName)
        .join(".");

    // TODO(csilvers): support GET-requests with batched queries.
    // NOTE(somewhatabstract): Batch queries are not currently ssr-safe

    const uri = getUri(
        {
            ...uriData,
            fastlyCachingValue: null,
        },
        operationNames,
        {},
    );
    return khanFetch(uri, options);
};

const buildHttpLink = () => {
    const getGraphqlUrl = (operation: any) => getGraphqUriData(operation);
    return ApolloLink.split(
        (operation) => {
            const context = operation.getContext();
            return context && context.useBatchingClient;
        },
        new BatchHttpLink({
            // @ts-expect-error - TS2322 - Type '(operation: any) => URIData' is not assignable to type 'string | UriFunction | undefined'.
            uri: getGraphqlUrl,
            // @ts-expect-error - TS2322 - Type '(uriData: URIData, options: any) => Promise<Response>' is not assignable to type '(input: RequestInfo, init?: RequestInit | undefined) => Promise<Response>'.
            fetch: batchFetchWithOpname,
        }),
        // @ts-expect-error - TS2322 - Type '(operation: any) => URIData' is not assignable to type 'string | UriFunction | undefined'. | TS2322 - Type '(uriData: URIData, options: any) => Promise<Response>' is not assignable to type '(input: RequestInfo, init?: RequestInit | undefined) => Promise<Response>'.
        createHttpLink({uri: getGraphqlUrl, fetch: fetchWithOpname}),
    );
};

/**
 * Determine what key to use when caching a client given its context.
 *
 * @param context The context for the created client. Defaults to locale "en".
 * @return The key to use for caching the client.
 */
const clientKeyFromContext = (context: Nullable<ClientContext>) => {
    const {targetLocale, curriculum}: ClientContext =
        context == null
            ? {
                  targetLocale: RequestInfo.kaLocale,
                  // NOTE(somewhatabstract, FEI-4906): We do not default the
                  // curriculum value here. This gives us a better cache hit
                  // rates for operations that don't need it. Operations that
                  // require the curriculum context value should explicitly
                  // provide it.
              }
            : context;

    // NOTE(somewhatabstract, FEI-4806): If we ever update the
    // `DeployInfo.commitSHA` value dynamically without a full server
    // navigation, we will need to update this to include that value, but
    // *only* when the fastlyCaching is set to `persist_until_publish`.

    // Now combine the locale and schema into a key.
    return `${targetLocale}:${curriculum ?? ""}`;
};

/**
 * Gets reference to an Apollo client instance.
 *
 * Despite the name, this does not create a new client on every call. Cached
 * clients can be returned if the one required already exists.
 *
 * @param [context] The context for the created client.
 * Can indicate the target locale. Defaults to "en".
 */
export default function createClient(
    context?: ClientContext,
): ApolloClientType {
    // Determine the key we will use to cache the client.
    const clientKey = clientKeyFromContext(context);

    // If we have an override or the client is already cached, return it.
    const singleton = _overrideApolloClientSingleton ?? singletons[clientKey];
    if (singleton != null) {
        return singleton;
    }

    const httpLink = buildHttpLink();
    const cache = createInMemoryCache().restore({});
    const createApolloClient: NewApolloClientFn =
        _overrideNewApolloClient == null
            ? (opts: ApolloClientOptions) => new ApolloClient(opts)
            : _overrideNewApolloClient;
    const newSingleton = createApolloClient({
        connectToDevTools: true,
        cache,
        link: errorLink.concat(httpLink),
    });
    singletons[clientKey] = newSingleton;
    return newSingleton;
}

// Note that if you call this function you will override the ApolloClient
// for all modules, not just the default module.
export function installOverrideClient(
    overrideClient: ApolloClientType,
): () => void {
    if (_overrideApolloClientSingleton != null) {
        Log.error(
            "Installing an override Apollo client when one is already in place.",
            Errors.Internal,
        );
    }

    _overrideApolloClientSingleton = overrideClient;

    // Return a function that undoes this override
    return () => {
        _overrideApolloClientSingleton = null;
    };
}

/**
 * Permanently replace (new ApolloClient) with a custom get/create method.
 *
 * This is used by SSR to ensure GraphQL queries are properly captured for
 * server-side data gathering and client-side hydration to work.
 *
 * @returns {() => void} Function that will clear the override and any cached
 * instances of the created client. This is called at the end of SSR to tidy
 * things up (WEB-2496).
 */
export function overrideNewApolloClient(
    createNewApolloClient: NewApolloClientFn,
): () => void {
    if (_overrideNewApolloClient != null) {
        /**
         * NOTE(somewhatabstract): This should only ever be called once. That
         * call should be inside the full-page rendering code that invokes the
         * SSR process.
         */
        throw new KAError(
            "new ApolloClient already overridden",
            Errors.Internal,
        );
    }

    _overrideNewApolloClient = createNewApolloClient;
    const clearUp = () => {
        _overrideNewApolloClient = null;
        _overrideApolloClientSingleton = null;
        for (const key of Object.keys(singletons)) {
            delete singletons[key];
        }
    };
    return clearUp;
}

/**
 * Reset the stores of all clients and restart all in-flight queries.
 */
export function resetClientStores() {
    for (const key of Object.keys(singletons)) {
        const client = singletons[key];
        client?.resetStore();
    }
}

/**
 * Deletes items from caches of specified clients.
 *
 * This is terribly naughty and we should not do this. However, we
 * don't have a good way of deleting specific data from the cache.
 * Take this into consideration if we ever start implementing caching to
 * replace Apollo. If we don't cache at the GQL level, then this becomes a
 * Redux/whatever store is used issue instead.
 *
 * HERE BE DRAGONS!
 */
export function unsafeDeleteMatchesFromCaches(
    locales: "all" | "current",
    itemPredicate: (itemKey: string) => boolean,
): void {
    // Now, iterate our singletons, and apply the item predicate to those
    // that match the given criteria.
    for (const key of Object.keys(singletons)) {
        const keyParts = key.split(":");

        // Right, it's a client we may want to manipulate. Let's check the
        // locales.
        if (locales === "current" && !keyParts.includes(RequestInfo.kaLocale)) {
            // We're only supposed to manipulate the current locale and
            // this isn't that, so skip it.
            continue;
        }

        // OK, the reason this is unsafe is that we're reaching into an
        // implementating detail of the InMemoryCache here. Naughty.
        // By doing so, we bypass quite a bit of Apollo's ability to keep
        // things like rendering up-to-date with the cache. It's all very
        // naughty.
        // For reference:
        // The `cache` of the ApolloClient is an InMemoryCache, which has
        // its own internal cache on the `data` property.
        // That then has `data` and methods to manipulate that data.
        // The ApolloCache doesn't explicitly declare the `data` property, hence
        // the `as any` cast here.
        const dataCache = (singletons[key]?.cache as any).data;
        if (dataCache == null) {
            // Should not happen, but be defensive.
            continue;
        }

        // We could delete in the loop here, possibly, but that feels
        // dangerous, especially since we're delving into internals we should
        // not be (a good argument to not use Apollo for caching like this).
        const toBeDeleted = Object.keys(dataCache.data).filter((key) =>
            itemPredicate(key),
        );

        // OK, let's delete what we have been asked to delete.
        for (const key of toBeDeleted) {
            dataCache.delete(key);
        }
    }
}
